import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import {
  useMountTransition,
  useScrollTransition,
  useUnMountTransition,
} from 'util/react-transition-component';
import { useChangeTransitionState } from '../../../util/react-transition-component/hooks/useChangeTransitionState';
import ScrollTransition from '../../unlisted/scroll-transition/ScrollTransition';
import type { TextProps } from './Text.data';
import styles from './Text.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './Text.transitions';
import { useIsChineseLanguage } from '../../../hooks/useIsChineseLanguage';
import { useStoryblokEditorContext } from '../../unlisted/StoryblokEditor';

export default function Text({
  as = 'p',
  size,
  className,
  childClassName,
  children,
  isVisible,
  transitionInType = 'splitLineRotateIn',
  transitionOutType = 'fadeOut',
  disableTriggerInOnScroll = true,
  transitionOnMount = false,
  transitionOnUnMount = false,
  canTransitionIn,
  parallax,
  scrollTransitionVars,
  enableChineseTransitionFallback = true,
  ref,
  ...props
}: TextProps): ReactElement {
  const localRef = useRef(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const elementRef = (ref || localRef) as RefObject<any>;
  const isChineseLanguage = useIsChineseLanguage();
  const { isInEditorMode } = useStoryblokEditorContext();

  const { transitionController } = useScrollTransition({
    scroll: {
      canTransitionIn,
      disableTriggerInOnScroll,
      trigger: elementRef,
      vars: scrollTransitionVars,
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        transitionInType: isInEditorMode ? 'none' : transitionInType,
        transitionOutType: isInEditorMode ? 'none' : transitionOutType,
        enableChineseTransitionFallback,
        isChineseLanguage,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  const Tag = as;

  useMountTransition(transitionController, transitionOnMount);
  useUnMountTransition(transitionController, transitionOnUnMount);
  useChangeTransitionState(transitionController, isVisible);

  const innerClassName = parallax ? childClassName : className;

  const innerTemplate = (
    <Tag
      key={isInEditorMode ? 'editor_mode' : ''}
      ref={elementRef}
      className={classNames(styles.text, styles[size], innerClassName)}
      {...props}
    >
      {children}
    </Tag>
  );

  let template = innerTemplate;
  if (parallax) {
    template = (
      <ScrollTransition
        as="div"
        type="parallax"
        config={parallax.config}
        trigger={parallax.trigger}
        vars={parallax.vars}
        className={classNames(className)}
      >
        {innerTemplate}
      </ScrollTransition>
    );
  }

  return template;
}
